import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Popover,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useDevAuth } from '../utils/useDevAuth';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WhitelistedDomains from '../components/WhitelistedDomains';
import OrgUsagePanel from '../components/OrgUsagePanel';
import ReadyForCollection from '../components/ReadyForCollection';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Organization {
  id: number;
  name: string;
  backendClientName?: string;
  dashboardType: string;
  dashboardStatus: string;
  masterSheetUrl?: string;
  deiTargetPillar?: string;
  trialWall: boolean;
  trialDuration: number | null;
  timezone: string;
  isCollecting: boolean;
  skipComments: boolean;
  platformFrequencies: Record<string, string> | null;
  priority: number | null;
  dataStartDate: string | null;
  dataEndDate: string | null;
}

interface MemberAccess {
  id: number;
  userEmail: string;
  role: string;
  isActive: boolean;
}

interface BrandMap {
  id: number;
  organizationId: number;
  platform: string;
  platformAccountName: string;
  platformAccountId: string | null;
  brandname: string;
  competitorType: string | null;
  accountIdsToMerge: any;
  locale: string | null;
}

interface KeywordEntry {
  id: number;
  organizationId: number;
  pillarName: string;
  keyword: string;
  dateAdded: string | null;
}

interface CollectionStatus {
  platform: string;
  accountName: string;
  accountId: string | null;
  earliestRequested: string | null;
  latestRequested: string | null;
  coveredStart: string | null;
  coveredEnd: string | null;
  completedChunks: number;
  totalChunks: number;
  totalPosts: number;
}

interface AccountAlert {
  id: number;
  createdAt: string | null;
  organizationId: number;
  platform: string;
  accountName: string;
  accountId: string | null;
  alertType: string;
  message: string;
  details: any;
  resolved: boolean;
  resolvedAt: string | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
];

const PLATFORMS = ['facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'youtube'] as const;
const FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'disabled'] as const;

// Platform URL patterns for display and slug extraction
const PLATFORM_URL_INFO: Record<string, { prefix: string; hint: string; buildUrl: (slug: string) => string; extractSlug: (input: string) => string; extractLocale?: (input: string) => string | null }> = {
  facebook: {
    prefix: 'facebook.com/',
    hint: 'facebook.com/...',
    buildUrl: (slug) => `https://facebook.com/${slug}`,
    extractSlug: (input) => {
      const raw = input.trim();
      // profile.php?id= URLs: keep the id, strip other params
      if (raw.includes('profile.php?id=')) {
        const match = raw.match(/profile\.php\?id=(\d+)/);
        return match ? `profile.php?id=${match[1]}` : raw;
      }
      let s = raw.replace(/\?.*$/, '').replace(/\/+$/, '');
      if (s.includes('/')) s = s.split('/').pop() || s;
      return s;
    },
    extractLocale: (input) => {
      const match = input.match(/[?&]locale=([a-zA-Z_]+)/);
      return match ? match[1] : null;
    },
  },
  instagram: {
    prefix: 'instagram.com/',
    hint: 'instagram.com/...',
    buildUrl: (slug) => `https://instagram.com/${slug}`,
    extractSlug: (input) => {
      let s = input.trim().replace(/\/+$/, '').replace('/?hl=en', '');
      if (s.includes('/')) s = s.split('/').pop() || s;
      return s;
    },
  },
  linkedin: {
    prefix: 'linkedin.com/...',
    hint: 'linkedin.com/company/...',
    buildUrl: (slug) => slug.includes('linkedin.com') ? slug : `https://linkedin.com/company/${slug}`,
    extractSlug: (input) => input.trim(), // LinkedIn keeps the full URL
  },
  tiktok: {
    prefix: 'tiktok.com/@',
    hint: 'tiktok.com/@...',
    buildUrl: (slug) => `https://tiktok.com/@${slug.replace(/^@/, '')}`,
    extractSlug: (input) => {
      let s = input.trim().replace(/\/+$/, '');
      if (s.includes('/')) s = s.split('/').pop() || s;
      return s.replace(/^@/, '');
    },
  },
  twitter: {
    prefix: 'x.com/',
    hint: 'x.com/...',
    buildUrl: (slug) => `https://x.com/${slug}`,
    extractSlug: (input) => {
      let s = input.trim().replace(/\/+$/, '');
      if (s.includes('/')) s = s.split('/').pop() || s;
      return s.replace(/^@/, '');
    },
  },
  youtube: {
    prefix: 'youtube.com/@',
    hint: 'youtube.com/@...',
    buildUrl: (slug) => `https://youtube.com/@${slug.replace(/^@/, '')}`,
    extractSlug: (input) => {
      let s = input.trim().replace(/\/+$/, '');
      if (s.includes('/')) s = s.split('/').pop() || s;
      return s.replace(/^@/, '');
    },
  },
};

// Spreadsheet-like cell styling
const cellSx = {
  cursor: 'default',
  px: 1.5,
  py: 0.75,
  fontSize: '0.875rem',
  borderRight: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap' as const,
  minWidth: 120,
  position: 'relative' as const,
  '&:hover .cell-delete': { opacity: 1 },
};
const headerCellSx = {
  ...cellSx,
  fontWeight: 600,
  textTransform: 'capitalize' as const,
  backgroundColor: 'grey.100',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OrganizationDetailsPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { user } = useDevAuth();

  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  // Org state
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [editedOrg, setEditedOrg] = useState<Organization | null>(null);

  // Members state
  const [members, setMembers] = useState<MemberAccess[]>([]);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('Viewer');
  const [editingMember, setEditingMember] = useState<MemberAccess | null>(null);
  const [openEditMember, setOpenEditMember] = useState(false);

  // Brand maps state
  const [brandMaps, setBrandMaps] = useState<BrandMap[]>([]);
  const [collectionStatus, setCollectionStatus] = useState<CollectionStatus[]>([]);
  const [accountAlerts, setAccountAlerts] = useState<AccountAlert[]>([]);
  const [linkedinStats, setLinkedinStats] = useState<any[]>([]);
  const [linkedinLogs, setLinkedinLogs] = useState<any[]>([]);
  const [showLinkedinLogs, setShowLinkedinLogs] = useState(false);

  // Keywords state
  const [keywords, setKeywords] = useState<KeywordEntry[]>([]);
  const [pillars, setPillars] = useState<Record<string, string[]>>({});

  // Tab for sub-sections
  const [activeTab, setActiveTab] = useState(0);

  // Inline editing state (shared — for brand names, keywords, pillar headers)
  const [editingCell, setEditingCell] = useState<{ type: 'brand' | 'keyword' | 'pillarHeader' | 'newBrand' | 'newKeyword' | 'newPillar'; brand?: string; platform?: string; pillar?: string; keywordId?: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Pending brands (newly created, no accounts yet — show as empty rows)
  const [pendingBrands, setPendingBrands] = useState<string[]>([]);
  const [pendingPillars, setPendingPillars] = useState<string[]>([]);

  // Account popover state (for social account cells)
  const [accountPopover, setAccountPopover] = useState<{
    anchorEl: HTMLElement | null;
    brand: string;
    platform: string;
    currentAccountName: string;
  } | null>(null);
  const [popoverInput, setPopoverInput] = useState('');
  const [popoverValidating, setPopoverValidating] = useState(false);
  const [popoverError, setPopoverError] = useState('');
  const popoverInputRef = useRef<HTMLInputElement>(null);

  // Conflict dialog
  const [conflictDialog, setConflictDialog] = useState<{
    open: boolean;
    brand: string;
    platform: string;
    newAccountName: string;
    existingId: number;
    existingAccountName: string;
  } | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message: string, severity: SnackbarState['severity'] = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchBrandMaps = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/brand-maps`);
      setBrandMaps(res.data.brandMaps || []);
    } catch (e) { console.error('Error loading brand maps:', e); }
  }, [orgId, axiosClient]);

  const fetchKeywords = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/keywords`);
      setKeywords(res.data.keywords || []);
      setPillars(res.data.pillars || {});
    } catch (e) { console.error('Error loading keywords:', e); }
  }, [orgId, axiosClient]);

  const fetchCollectionStatus = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/collection-status`);
      setCollectionStatus(res.data.collectionStatus || []);
    } catch (e) { console.error('Error loading collection status:', e); }
  }, [orgId, axiosClient]);

  const fetchAccountAlerts = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/account-alerts`);
      setAccountAlerts(res.data.alerts || []);
    } catch (e) { console.error('Error loading account alerts:', e); }
  }, [orgId, axiosClient]);

  const fetchLinkedinStats = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/linkedin-stats`);
      setLinkedinStats(res.data.accounts || []);
    } catch (e) { console.error('Error loading LinkedIn stats:', e); }
  }, [orgId, axiosClient]);

  const fetchLinkedinLogs = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/linkedin-collection-log`);
      setLinkedinLogs(res.data.logs || []);
    } catch (e) { console.error('Error loading LinkedIn logs:', e); }
  }, [orgId, axiosClient]);

  useEffect(() => {
    if (!isAdmin || !orgId) return;

    const fetchData = async () => {
      try {
        const [orgRes, accessRes] = await Promise.all([
          axiosClient.get('/api/admin/organizations'),
          axiosClient.get('/api/admin/organization-access'),
        ]);
        const orgData = (orgRes.data.organizations as Organization[]).find(o => String(o.id) === orgId);
        if (orgData) {
          setOrganization(orgData);
          setEditedOrg(orgData);
        }
        const orgMembers = (accessRes.data.access_records || []).filter((m: any) => String(m.organizationId) === orgId)
          .map((m: any) => ({
            id: m.id,
            userEmail: m.userEmail,
            role: m.role,
            isActive: m.isActive,
          }));
        setMembers(orgMembers);
      } catch (error) {
        console.error('Error loading organization details:', error);
      }
    };

    fetchData();
    fetchBrandMaps();
    fetchKeywords();
    fetchCollectionStatus();
    fetchAccountAlerts();
    fetchLinkedinStats();
  }, [isAdmin, orgId, axiosClient, fetchBrandMaps, fetchKeywords, fetchCollectionStatus, fetchAccountAlerts, fetchLinkedinStats]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  // -------------------------------------------------------------------------
  // Org handlers
  // -------------------------------------------------------------------------

  const handleSaveOrg = async () => {
    if (!editedOrg) return;
    try {
      await axiosClient.put(`/api/admin/organizations/${editedOrg.id}`, editedOrg);
      setOrganization(editedOrg);
      showSnackbar('Organization updated!', 'success');
    } catch (error) {
      console.error('Error saving organization:', error);
      showSnackbar('Failed to save organization', 'error');
    }
  };

  const updateFrequency = (platform: string, value: string) => {
    if (!editedOrg) return;
    const freqs = { ...(editedOrg.platformFrequencies || {}) };
    freqs[platform] = value;
    setEditedOrg({ ...editedOrg, platformFrequencies: freqs });
  };

  // -------------------------------------------------------------------------
  // Member handlers
  // -------------------------------------------------------------------------

  const handleAddMember = async () => {
    try {
      await axiosClient.post('/api/admin/organization-access', {
        userEmail: newMemberEmail,
        organizationId: Number(orgId),
        role: newMemberRole,
        isTrial: false,
      });
      setMembers([...members, { id: Date.now(), userEmail: newMemberEmail, role: newMemberRole, isActive: true }]);
      setNewMemberEmail('');
      setNewMemberRole('Viewer');
      setOpenAddMember(false);
    } catch (error) {
      console.error('Error adding member:', error);
      showSnackbar('Failed to add member', 'error');
    }
  };

  const handleEditMemberSave = async () => {
    if (!editingMember) return;
    try {
      await axiosClient.put(`/api/admin/organization-access/${editingMember.id}`, {
        role: editingMember.role,
        isActive: editingMember.isActive,
        userEmail: editingMember.userEmail,
      });
      setMembers(members.map(m => m.id === editingMember.id ? editingMember : m));
      setOpenEditMember(false);
    } catch (error) {
      console.error('Error updating member:', error);
      showSnackbar('Failed to update member', 'error');
    }
  };

  const handleDeleteMember = async (memberId: number) => {
    if (!window.confirm('Remove this member from organization?')) return;
    try {
      await axiosClient.delete(`/api/admin/organization-access/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
    } catch (error) {
      console.error('Error deleting member:', error);
      showSnackbar('Failed to delete member', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Brand map inline editing helpers
  // -------------------------------------------------------------------------

  const startEditCell = (type: NonNullable<typeof editingCell>['type'], opts: Omit<NonNullable<typeof editingCell>, 'type'>, currentValue: string) => {
    setEditingCell({ type, ...opts });
    setEditValue(currentValue);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Get the BrandMap entry for a given brand + platform
  const getBrandMapEntry = (brand: string, platform: string): BrandMap | undefined =>
    brandMaps.find(bm => bm.brandname === brand && bm.platform === platform);

  // Get collection status for a brand map entry (match by platform + account_id or account_name)
  const getCollectionStatus = (entry: BrandMap | undefined): CollectionStatus | undefined => {
    if (!entry) return undefined;
    return collectionStatus.find(cs =>
      cs.platform === entry.platform &&
      (cs.accountId === entry.platformAccountId || cs.accountName === entry.platformAccountName)
    );
  };

  // Effective end of the org's declared collection range as a YYYY-MM-DD string,
  // or null when there is no concrete target to compare against (end not set).
  // "ongoing" means "collect up to now"; collection runs nightly, so the freshest
  // data we expect is ~1 day old -> the up-to-date target is yesterday.
  const getCollectionRangeEnd = (): string | null => {
    const raw = (organization?.dataEndDate || '').trim().toLowerCase();
    if (!raw) return null;
    if (raw === 'ongoing') {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    }
    return organization!.dataEndDate!.slice(0, 10);
  };

  // Coverage level for an account: 'complete' (green), 'partial' (amber), 'none' (red).
  // Green requires BOTH: the covered span fills the requested range, AND the covered
  // data reaches the org's declared collection-range end. The second criterion stops a
  // stale account whose requested range simply stops short of "ongoing" (e.g. org 123's
  // IG accounts ending in April) from showing green.
  const getCoverageLevel = (cs: CollectionStatus | undefined): 'complete' | 'partial' | 'none' => {
    if (!cs || !cs.coveredStart) return 'none';
    const spansRequested = !!(cs.coveredEnd && cs.earliestRequested && cs.latestRequested
      && cs.coveredStart <= cs.earliestRequested && cs.coveredEnd >= cs.latestRequested);
    const rangeEnd = getCollectionRangeEnd();
    const reachesRangeEnd = !rangeEnd || !!(cs.coveredEnd && cs.coveredEnd.slice(0, 10) >= rangeEnd);
    return spansRequested && reachesRangeEnd ? 'complete' : 'partial';
  };

  // Format a date string to short format
  const fmtDate = (iso: string | null): string => {
    if (!iso) return 'n/a';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  // -------------------------------------------------------------------------
  // Account popover helpers
  // -------------------------------------------------------------------------

  const openAccountPopover = (anchorEl: HTMLElement, brand: string, platform: string) => {
    const entry = getBrandMapEntry(brand, platform);
    const currentName = entry?.platformAccountName || '';
    setAccountPopover({ anchorEl, brand, platform, currentAccountName: currentName });
    setPopoverInput(currentName);
    setPopoverError('');
    setPopoverValidating(false);
    // Focus the input after the popover renders
    setTimeout(() => popoverInputRef.current?.focus(), 100);
  };

  const closeAccountPopover = () => {
    setAccountPopover(null);
    setPopoverInput('');
    setPopoverError('');
    setPopoverValidating(false);
  };

  // Extract slug from whatever the user typed (URL or slug)
  const getPopoverSlug = (): string => {
    if (!accountPopover) return '';
    const info = PLATFORM_URL_INFO[accountPopover.platform];
    if (!info) return popoverInput.trim();
    return info.extractSlug(popoverInput);
  };

  // Live preview URL based on current input
  const getPopoverPreviewUrl = (): string => {
    if (!accountPopover) return '';
    const info = PLATFORM_URL_INFO[accountPopover.platform];
    const slug = getPopoverSlug();
    if (!slug) return '';
    return info ? info.buildUrl(slug) : slug;
  };

  // Extract locale from URL input (Facebook only, for Global Pages)
  const getPopoverLocale = (): string | null => {
    if (!accountPopover) return null;
    const info = PLATFORM_URL_INFO[accountPopover.platform];
    return info?.extractLocale ? info.extractLocale(popoverInput) : null;
  };

  // Save from popover (validate → create or update)
  const handlePopoverSave = async () => {
    if (!accountPopover) return;
    const { brand, platform } = accountPopover;
    const slug = getPopoverSlug();
    const locale = getPopoverLocale();
    const existing = getBrandMapEntry(brand, platform);

    // If cleared, delete the entry
    if (!slug && existing) {
      try {
        await axiosClient.delete(`/api/admin/organizations/${orgId}/brand-maps/${existing.id}`);
        setBrandMaps(prev => prev.filter(b => b.id !== existing.id));
        showSnackbar(`Deleted ${platform} account for ${brand}`, 'success');
      } catch (e: any) {
        showSnackbar(e.response?.data?.error || 'Failed to delete', 'error');
      }
      closeAccountPopover();
      return;
    }

    if (!slug) {
      closeAccountPopover();
      return;
    }

    // Same value — no change
    if (existing && existing.platformAccountName === slug) {
      closeAccountPopover();
      return;
    }

    setPopoverValidating(true);
    setPopoverError('');

    try {
      const valRes = await axiosClient.post('/api/admin/validate-account', {
        platform,
        accountName: slug,
        organizationId: Number(orgId),
      });

      if (!valRes.data.valid) {
        setPopoverError(valRes.data.error || 'Account not found');
        setPopoverValidating(false);
        return; // keep popover open
      }

      const accountId = valRes.data.accountId;

      if (existing) {
        const res = await axiosClient.put(`/api/admin/organizations/${orgId}/brand-maps/${existing.id}`, {
          platformAccountName: slug,
          platformAccountId: accountId,
          locale,
        });
        setBrandMaps(prev => prev.map(b => b.id === existing.id ? res.data : b));
        showSnackbar(`Updated ${platform} account for ${brand}`, 'success');
      } else {
        try {
          const res = await axiosClient.post(`/api/admin/organizations/${orgId}/brand-maps`, {
            platform,
            platformAccountName: slug,
            platformAccountId: accountId,
            brandname: brand,
            locale,
          });
          setBrandMaps(prev => [...prev, res.data]);
          setPendingBrands(prev => prev.filter(b => b !== brand));
          showSnackbar(`Added ${platform} account for ${brand}`, 'success');
        } catch (err: any) {
          if (err.response?.status === 409) {
            setConflictDialog({
              open: true,
              brand,
              platform,
              newAccountName: slug,
              existingId: err.response.data.detail.existingId,
              existingAccountName: err.response.data.detail.existingAccountName,
            });
            setPopoverValidating(false);
            return;
          }
          throw err;
        }
      }
      closeAccountPopover();
    } catch (e: any) {
      if (!conflictDialog) {
        setPopoverError(e.response?.data?.detail?.error || e.response?.data?.error || e.message || 'Error saving account');
      }
      setPopoverValidating(false);
    }
  };

  // Handle conflict resolution: replace existing
  const handleConflictReplace = async () => {
    if (!conflictDialog) return;
    const { existingId, platform, brand, newAccountName } = conflictDialog;
    try {
      const valRes = await axiosClient.post('/api/admin/validate-account', {
        platform,
        accountName: newAccountName,
        organizationId: Number(orgId),
      });
      const accountId = valRes.data.valid ? valRes.data.accountId : null;

      const res = await axiosClient.put(`/api/admin/organizations/${orgId}/brand-maps/${existingId}`, {
        platformAccountName: newAccountName,
        platformAccountId: accountId,
      });
      setBrandMaps(prev => prev.map(b => b.id === existingId ? res.data : b));
      showSnackbar(`Replaced ${platform} account for ${brand}`, 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || 'Failed to replace', 'error');
    }
    setConflictDialog(null);
    closeAccountPopover();
  };

  // Save brand name edit (rename all entries for that brand)
  const saveBrandName = async (oldBrand: string, newBrand: string) => {
    const trimmed = newBrand.trim();
    if (!trimmed || trimmed === oldBrand) {
      cancelEdit();
      return;
    }
    const entriesToUpdate = brandMaps.filter(bm => bm.brandname === oldBrand);
    try {
      for (const entry of entriesToUpdate) {
        await axiosClient.put(`/api/admin/organizations/${orgId}/brand-maps/${entry.id}`, {
          brandname: trimmed,
        });
      }
      setBrandMaps(prev => prev.map(b => b.brandname === oldBrand ? { ...b, brandname: trimmed } : b));
      showSnackbar(`Renamed brand "${oldBrand}" to "${trimmed}"`, 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || 'Failed to rename brand', 'error');
    }
    cancelEdit();
  };

  // Add new brand row (creates a pending brand that shows as an empty row)
  const saveNewBrand = async (brandName: string) => {
    const trimmed = brandName.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    setPendingBrands(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    cancelEdit();
    showSnackbar(`Brand "${trimmed}" added. Double-click a platform cell to add an account.`, 'info');
  };

  const handleDeleteBrandMap = async (mapId: number) => {
    try {
      await axiosClient.delete(`/api/admin/organizations/${orgId}/brand-maps/${mapId}`);
      setBrandMaps(prev => prev.filter(b => b.id !== mapId));
      showSnackbar('Account deleted', 'success');
    } catch (error) {
      console.error('Error deleting brand map:', error);
      showSnackbar('Failed to delete social account', 'error');
    }
  };

  const handleDeleteBrand = async (brand: string) => {
    const rowsForBrand = brandMaps.filter(b => b.brandname === brand);
    const accountCount = rowsForBrand.length;
    const warning = accountCount > 0
      ? `Delete brand "${brand}" and all ${accountCount} social account row(s) for this organization?\n\nThis is IRREVERSIBLE. Collected post/comment data in BigQuery will remain, but the brand will be removed from collection going forward.`
      : `Remove pending brand "${brand}"?`;
    if (!window.confirm(warning)) return;
    try {
      if (accountCount > 0) {
        await axiosClient.post(`/api/admin/organizations/${orgId}/brand-maps/delete-brand`, { brandname: brand });
        setBrandMaps(prev => prev.filter(b => b.brandname !== brand));
      }
      setPendingBrands(prev => prev.filter(b => b !== brand));
      showSnackbar(`Brand "${brand}" deleted`, 'success');
    } catch (error: any) {
      console.error('Error deleting brand:', error);
      showSnackbar(error.response?.data?.error || 'Failed to delete brand', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Keyword inline editing helpers
  // -------------------------------------------------------------------------

  const saveKeywordEdit = async (keywordId: number, newValue: string) => {
    const trimmed = newValue.trim();
    const entry = keywords.find(k => k.id === keywordId);
    if (!entry) { cancelEdit(); return; }

    if (!trimmed) {
      // Delete
      try {
        await axiosClient.delete(`/api/admin/organizations/${orgId}/keywords/${keywordId}`);
        setKeywords(prev => prev.filter(k => k.id !== keywordId));
        fetchKeywords();
        showSnackbar('Keyword deleted', 'success');
      } catch (e: any) {
        showSnackbar(e.response?.data?.error || 'Failed to delete keyword', 'error');
      }
      cancelEdit();
      return;
    }

    if (trimmed === entry.keyword) {
      cancelEdit();
      return;
    }

    try {
      const res = await axiosClient.put(`/api/admin/organizations/${orgId}/keywords/${keywordId}`, {
        keyword: trimmed,
      });
      setKeywords(prev => prev.map(k => k.id === keywordId ? res.data : k));
      fetchKeywords();
      showSnackbar('Keyword updated', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || 'Failed to update keyword', 'error');
    }
    cancelEdit();
  };

  const saveNewKeyword = async (pillar: string, keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    try {
      const res = await axiosClient.post(`/api/admin/organizations/${orgId}/keywords`, {
        pillarName: pillar,
        keyword: trimmed,
      });
      setKeywords(prev => [...prev, res.data]);
      setPendingPillars(prev => prev.filter(p => p !== pillar));
      fetchKeywords();
      showSnackbar(`Added keyword "${trimmed}" to ${pillar}`, 'success');
      // Stay in add mode for quick batch entry
      setEditValue('');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || e.response?.data?.detail?.error || 'Failed to add keyword', 'error');
    }
  };

  const savePillarRename = async (oldPillar: string, newPillar: string) => {
    const trimmed = newPillar.trim();
    if (!trimmed || trimmed === oldPillar) {
      cancelEdit();
      return;
    }
    // Rename all keywords in this pillar using bulk update
    const updatedPillars = { ...pillars };
    updatedPillars[trimmed] = updatedPillars[oldPillar] || [];
    delete updatedPillars[oldPillar];

    try {
      await axiosClient.put(`/api/admin/organizations/${orgId}/keywords/bulk`, {
        pillars: updatedPillars,
      });
      fetchKeywords();
      showSnackbar(`Renamed pillar "${oldPillar}" to "${trimmed}"`, 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || 'Failed to rename pillar', 'error');
    }
    cancelEdit();
  };

  const saveNewPillar = async (pillarName: string) => {
    const trimmed = pillarName.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    setPendingPillars(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    setEditingCell({ type: 'newKeyword', pillar: trimmed });
    setEditValue('');
  };

  const handleDeleteKeyword = async (keywordId: number) => {
    try {
      await axiosClient.delete(`/api/admin/organizations/${orgId}/keywords/${keywordId}`);
      setKeywords(prev => prev.filter(k => k.id !== keywordId));
      fetchKeywords();
      showSnackbar('Keyword deleted', 'success');
    } catch (error) {
      console.error('Error deleting keyword:', error);
      showSnackbar('Failed to delete keyword', 'error');
    }
  };

  const handleDeletePillar = async (pillar: string) => {
    if (!window.confirm(`Delete pillar "${pillar}" and all its keywords?`)) return;
    if (!(pillar in pillars)) {
      setPendingPillars(prev => prev.filter(p => p !== pillar));
      return;
    }
    const updatedPillars = { ...pillars };
    delete updatedPillars[pillar];
    try {
      await axiosClient.put(`/api/admin/organizations/${orgId}/keywords/bulk`, {
        pillars: updatedPillars,
      });
      setPendingPillars(prev => prev.filter(p => p !== pillar));
      fetchKeywords();
      showSnackbar(`Deleted pillar "${pillar}"`, 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || 'Failed to delete pillar', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Account alert handlers
  // -------------------------------------------------------------------------

  const handleResolveAlert = async (alertId: number) => {
    try {
      await axiosClient.put(`/api/admin/account-alerts/${alertId}/resolve`);
      setAccountAlerts(prev => prev.map(a => a.id === alertId ? { ...a, resolved: true, resolvedAt: new Date().toISOString() } : a));
      showSnackbar('Alert resolved', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.error || 'Failed to resolve alert', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Key handler for inline editing
  // -------------------------------------------------------------------------

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      cancelEdit();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    }
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { type } = editingCell;
    if (type === 'brand') {
      saveBrandName(editingCell.brand!, editValue);
    } else if (type === 'newBrand') {
      saveNewBrand(editValue);
    } else if (type === 'keyword') {
      saveKeywordEdit(editingCell.keywordId!, editValue);
    } else if (type === 'newKeyword') {
      saveNewKeyword(editingCell.pillar!, editValue);
    } else if (type === 'pillarHeader') {
      savePillarRename(editingCell.pillar!, editValue);
    } else if (type === 'newPillar') {
      saveNewPillar(editValue);
    }
  };

  // -------------------------------------------------------------------------
  // Render: inline edit cell helper
  // -------------------------------------------------------------------------

  const renderEditableCell = (isEditing: boolean, displayValue: string, onDoubleClick: () => void, bgColor?: string) => {
    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            inputRef={editInputRef}
            size="small"
            variant="standard"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={handleCellKeyDown}
            onBlur={() => commitEdit()}
            fullWidth
            sx={{ '& input': { fontSize: '0.875rem', py: 0 } }}
            autoFocus
          />
        </Box>
      );
    }
    return (
      <Box
        onDoubleClick={onDoubleClick}
        sx={{
          minHeight: 24,
          cursor: 'text',
          userSelect: 'none',
          backgroundColor: bgColor,
          borderRadius: bgColor ? 0.5 : 0,
          px: bgColor ? 0.5 : 0,
        }}
      >
        {displayValue}
      </Box>
    );
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Loading organization...</Typography>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Derived data for pivoted tables
  // -------------------------------------------------------------------------

  // Unique brand names (including pending brands with no accounts yet)
  const uniqueBrands = Array.from(new Set([...brandMaps.map(bm => bm.brandname), ...pendingBrands])).sort();

  // Pillar names and keyword lookup
  const existingPillars = Array.from(new Set([...Object.keys(pillars), ...pendingPillars]));
  const keywordsByPillar: Record<string, KeywordEntry[]> = {};
  existingPillars.forEach(p => {
    keywordsByPillar[p] = keywords.filter(k => k.pillarName === p);
  });
  const maxKeywordRows = Math.max(0, ...existingPillars.map(p => keywordsByPillar[p]?.length || 0));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display:'flex', gap:2, mb:2 }}>
        <Button variant="outlined" onClick={() => navigate('/admin')}>Back to Organizations</Button>
        <Button component={RouterLink} to={`/org/${orgId}`} variant="contained" color="secondary" target="_blank" rel="noopener">
          Open Dashboard
        </Button>
      </Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Organization Details - {organization.name}</Typography>

      {/* ================================================================= */}
      {/* Org edit form                                                      */}
      {/* ================================================================= */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>General Settings</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Name" fullWidth value={editedOrg?.name || ''} onChange={e => setEditedOrg({ ...(editedOrg as Organization), name: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Backend Name" fullWidth value={editedOrg?.backendClientName || ''} onChange={e => setEditedOrg({ ...(editedOrg as Organization), backendClientName: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Dashboard Type</InputLabel>
              <Select value={editedOrg?.dashboardType || ''} label="Dashboard Type" onChange={e => setEditedOrg({ ...(editedOrg as Organization), dashboardType: e.target.value })}>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={editedOrg?.dashboardStatus || ''} label="Status" onChange={e => setEditedOrg({ ...(editedOrg as Organization), dashboardStatus: e.target.value })}>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Master Sheet URL" fullWidth value={editedOrg?.masterSheetUrl || ''} onChange={e => setEditedOrg({ ...(editedOrg as Organization), masterSheetUrl: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={editedOrg?.timezone || 'America/New_York'}
                label="Timezone"
                onChange={e => setEditedOrg({ ...(editedOrg as Organization), timezone: e.target.value })}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              type="number"
              label="Priority"
              fullWidth
              value={editedOrg?.priority ?? ''}
              onChange={e => setEditedOrg({ ...(editedOrg as Organization), priority: parseInt(e.target.value) || null })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="DEI Target Pillar"
              fullWidth
              value={editedOrg?.deiTargetPillar || ''}
              onChange={e => setEditedOrg({ ...(editedOrg as Organization), deiTargetPillar: e.target.value })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>Data Collection</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={editedOrg?.isCollecting || false} onChange={e => setEditedOrg({ ...(editedOrg as Organization), isCollecting: e.target.checked })} />}
              label="Collecting Data"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={editedOrg?.skipComments || false} onChange={e => setEditedOrg({ ...(editedOrg as Organization), skipComments: e.target.checked })} />}
              label="Skip Comments"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={editedOrg?.trialWall || false} onChange={e => setEditedOrg({ ...(editedOrg as Organization), trialWall: e.target.checked })} />}
              label="Trial Wall"
            />
          </Grid>
          {editedOrg?.trialWall && (
            <Grid item xs={12} sm={4}>
              <TextField type="number" label="Trial Duration (days)" fullWidth value={editedOrg?.trialDuration || ''} onChange={e => setEditedOrg({ ...(editedOrg as Organization), trialDuration: parseInt(e.target.value) || null })} />
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <TextField label="Data Start Date" fullWidth placeholder="YYYY-MM-DD or leave blank" value={editedOrg?.dataStartDate || ''} onChange={e => setEditedOrg({ ...(editedOrg as Organization), dataStartDate: e.target.value || null })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Data End Date" fullWidth placeholder="YYYY-MM-DD or 'ongoing'" value={editedOrg?.dataEndDate || ''} onChange={e => setEditedOrg({ ...(editedOrg as Organization), dataEndDate: e.target.value || null })} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>Platform Frequencies</Typography>
        <Grid container spacing={2}>
          {PLATFORMS.map(p => (
            <Grid item xs={6} sm={4} md={2} key={p}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ textTransform: 'capitalize' }}>{p}</InputLabel>
                <Select
                  value={editedOrg?.platformFrequencies?.[p] || 'disabled'}
                  label={p}
                  onChange={e => updateFrequency(p, e.target.value)}
                >
                  {FREQUENCY_OPTIONS.map(f => (
                    <MenuItem key={f} value={f} sx={{ textTransform: 'capitalize' }}>{f}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}
        </Grid>

        {editedOrg?.platformFrequencies?.linkedin && editedOrg.platformFrequencies.linkedin !== 'disabled' && (
          <TextField
            type="number"
            size="small"
            label="LinkedIn Max Posts per Brand"
            value={editedOrg?.platformFrequencies?.linkedin_max_posts ?? 250}
            onChange={e => {
              const freqs = { ...(editedOrg.platformFrequencies || {}) };
              freqs.linkedin_max_posts = e.target.value;
              setEditedOrg({ ...editedOrg, platformFrequencies: freqs });
            }}
            helperText="Ceiling for dynamic post count. Default: 250."
            inputProps={{ min: 1, max: 500 }}
            sx={{ mt: 2, width: 250 }}
          />
        )}

        <Box sx={{ mt: 3, textAlign: 'right' }}>
          <Button variant="contained" onClick={handleSaveOrg}>Save Organization</Button>
        </Box>
      </Paper>

      {/* ================================================================= */}
      {/* Tabbed sub-sections                                                */}
      {/* ================================================================= */}
      <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); cancelEdit(); }} sx={{ mb: 2 }}>
        <Tab label={`Members (${members.length})`} />
        <Tab label={`Social Accounts (${brandMaps.length})`} />
        <Tab label={`Pillars & Keywords (${keywords.length})`} />
        <Tab label={`Data Collection${accountAlerts.filter(a => !a.resolved).length > 0 ? ` (${accountAlerts.filter(a => !a.resolved).length})` : ''}`} />
        <Tab label="Whitelisted Domains" />
        <Tab label="Usage" />
      </Tabs>

      {/* ================================================================= */}
      {/* Tab 0: Members                                                     */}
      {/* ================================================================= */}
      {activeTab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">Members</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddMember(true)}>Add Member</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.userEmail}</TableCell>
                    <TableCell>{m.role}</TableCell>
                    <TableCell>{m.isActive ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => { setEditingMember({ ...m }); setOpenEditMember(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteMember(m.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ================================================================= */}
      {/* Tab 1: Social Accounts — Pivoted Table                             */}
      {/* ================================================================= */}
      {activeTab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5">Social Accounts</Typography>
            <Typography variant="body2" color="text.secondary">Click a cell to edit. Paste a URL or type an account name.</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Collection range: <strong>{organization.dataStartDate || 'not set'}</strong> to <strong>{organization.dataEndDate || 'not set'}</strong>
          </Typography>
          <ReadyForCollection
            orgId={organization.id}
            refreshKey={brandMaps}
            hasAccounts={brandMaps.length > 0}
            onNotify={showSnackbar}
          />
          <Alert severity="warning" sx={{ mb: 2 }}>Without "Ready for collection", changes take effect with the next nightly run. Adding or changing an account will trigger a full data fill for that account.</Alert>
          <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, minWidth: 160, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'grey.200' }}>Brand</TableCell>
                  {PLATFORMS.map(p => (
                    <TableCell key={p} sx={{ ...headerCellSx, lineHeight: 1.3 }}>
                      <Box sx={{ textTransform: 'capitalize' }}>{p}</Box>
                      <Box sx={{ fontSize: '0.7rem', fontWeight: 400, color: 'text.secondary', textTransform: 'none' }}>
                        {PLATFORM_URL_INFO[p]?.hint}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...headerCellSx, minWidth: 40, borderRight: 'none' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {uniqueBrands.map(brand => (
                  <TableRow key={brand} hover>
                    {/* Brand name cell */}
                    <TableCell sx={{ ...cellSx, fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1, pr: 4 }}>
                      {renderEditableCell(
                        editingCell?.type === 'brand' && editingCell.brand === brand,
                        brand,
                        () => startEditCell('brand', { brand }, brand),
                      )}
                      <Tooltip arrow title="Delete brand row (irreversible)">
                        <IconButton
                          className="cell-delete"
                          size="small"
                          sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, transition: 'opacity 0.2s', p: 0.25 }}
                          onClick={(e) => { e.stopPropagation(); handleDeleteBrand(brand); }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    {/* Platform cells — click opens popover */}
                    {PLATFORMS.map(platform => {
                      const entry = getBrandMapEntry(brand, platform);
                      const cs = getCollectionStatus(entry);
                      // Green = covered spans the requested range AND reaches the org's
                      // collection-range end; amber = partial; red = no coverage.
                      const level = cs ? getCoverageLevel(cs) : undefined;
                      const statusColor = level === 'complete' ? '#4caf50'
                        : level === 'partial' ? '#ff9800'
                        : level === 'none' ? '#ef5350'
                        : undefined;
                      const isPopoverTarget = accountPopover?.brand === brand && accountPopover?.platform === platform;
                      return (
                        <TableCell key={platform} sx={cellSx}>
                          <Box
                            onClick={(e) => openAccountPopover(e.currentTarget as HTMLElement, brand, platform)}
                            sx={{
                              minHeight: 24,
                              cursor: 'pointer',
                              userSelect: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              outline: isPopoverTarget ? '2px solid' : 'none',
                              outlineColor: 'primary.main',
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: entry ? undefined : 'action.hover',
                              },
                            }}
                          >
                            {entry ? (
                              <>
                                {statusColor && (
                                  <Tooltip
                                    arrow
                                    title={
                                      <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                                        <div>Covered: {fmtDate(cs!.coveredStart)} - {fmtDate(cs!.coveredEnd)}</div>
                                        <div>Requested: {fmtDate(cs!.earliestRequested)} - {fmtDate(cs!.latestRequested)}</div>
                                        <div>{cs!.totalPosts.toLocaleString()} posts</div>
                                      </Box>
                                    }
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: statusColor,
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                <Box component="span" sx={{
                                  backgroundColor: entry.platformAccountId ? 'rgba(76, 175, 80, 0.12)' : 'rgba(255, 193, 7, 0.12)',
                                  borderRadius: 2,
                                  px: 0.75,
                                  py: 0.25,
                                }}>
                                  {entry.platformAccountName}
                                </Box>
                              </>
                            ) : (
                              <Typography component="span" sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>+</Typography>
                            )}
                          </Box>
                          {/* Delete icon on hover */}
                          {entry && (
                            <IconButton
                              className="cell-delete"
                              size="small"
                              sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, transition: 'opacity 0.2s', p: 0.25 }}
                              onClick={() => handleDeleteBrandMap(entry.id)}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ ...cellSx, borderRight: 'none' }} />
                  </TableRow>
                ))}
                {/* Add brand row */}
                <TableRow>
                  <TableCell sx={{ ...cellSx, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                    {editingCell?.type === 'newBrand' ? (
                      <TextField
                        inputRef={editInputRef}
                        size="small"
                        variant="standard"
                        placeholder="Brand name..."
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={handleCellKeyDown}
                        onBlur={() => commitEdit()}
                        fullWidth
                        sx={{ '& input': { fontSize: '0.875rem', py: 0 } }}
                        autoFocus
                      />
                    ) : (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => startEditCell('newBrand', {}, '')}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Add brand
                      </Button>
                    )}
                  </TableCell>
                  {PLATFORMS.map(p => <TableCell key={p} sx={cellSx} />)}
                  <TableCell sx={{ ...cellSx, borderRight: 'none' }} />
                </TableRow>
                {uniqueBrands.length === 0 && !editingCell && (
                  <TableRow>
                    <TableCell colSpan={PLATFORMS.length + 2} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No social accounts configured. Click "Add brand" to start.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ================================================================= */}
      {/* Tab 2: Pillars & Keywords — Pivoted Table                          */}
      {/* ================================================================= */}
      {activeTab === 2 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5">Pillars & Keywords</Typography>
            <Typography variant="body2" color="text.secondary">Double-click a cell to edit. Enter to save, Escape to cancel.</Typography>
          </Box>
          <Alert severity="warning" sx={{ mb: 2 }}>Changes will take effect the next day.</Alert>
          <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {existingPillars.map(pillar => (
                    <TableCell key={pillar} sx={{ ...headerCellSx, minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {editingCell?.type === 'pillarHeader' && editingCell.pillar === pillar ? (
                          <TextField
                            inputRef={editInputRef}
                            size="small"
                            variant="standard"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={handleCellKeyDown}
                            onBlur={() => commitEdit()}
                            fullWidth
                            sx={{ '& input': { fontSize: '0.875rem', fontWeight: 600, py: 0 } }}
                            autoFocus
                          />
                        ) : (
                          <Tooltip title="Double-click to rename">
                            <Box
                              onDoubleClick={() => startEditCell('pillarHeader', { pillar }, pillar)}
                              sx={{ cursor: 'text', flex: 1 }}
                            >
                              {pillar}
                            </Box>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePillar(pillar)}
                          sx={{ ml: 0.5, p: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  ))}
                  {/* Add pillar column */}
                  <TableCell sx={{ ...headerCellSx, minWidth: 140, borderRight: 'none' }}>
                    {editingCell?.type === 'newPillar' ? (
                      <TextField
                        inputRef={editInputRef}
                        size="small"
                        variant="standard"
                        placeholder="Pillar name..."
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={handleCellKeyDown}
                        onBlur={() => commitEdit()}
                        fullWidth
                        sx={{ '& input': { fontSize: '0.875rem', fontWeight: 600, py: 0 } }}
                        autoFocus
                      />
                    ) : (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => startEditCell('newPillar', {}, '')}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Add pillar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: maxKeywordRows + 1 }).map((_, rowIdx) => (
                  <TableRow key={rowIdx} hover>
                    {existingPillars.map(pillar => {
                      const pillarKws = keywordsByPillar[pillar] || [];
                      const kw = pillarKws[rowIdx];
                      const isAddRow = rowIdx === pillarKws.length;
                      const isEditing = kw && editingCell?.type === 'keyword' && editingCell.keywordId === kw.id;
                      const isAddingHere = editingCell?.type === 'newKeyword' && editingCell.pillar === pillar && isAddRow;

                      if (isAddRow) {
                        return (
                          <TableCell key={pillar} sx={cellSx}>
                            {isAddingHere ? (
                              <TextField
                                inputRef={editInputRef}
                                size="small"
                                variant="standard"
                                placeholder="New keyword..."
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={handleCellKeyDown}
                                onBlur={() => { if (editValue.trim()) commitEdit(); else cancelEdit(); }}
                                fullWidth
                                sx={{ '& input': { fontSize: '0.875rem', py: 0 } }}
                                autoFocus
                              />
                            ) : (
                              <Box
                                onDoubleClick={() => startEditCell('newKeyword', { pillar }, '')}
                                sx={{ minHeight: 24, cursor: 'text', color: 'text.disabled', fontSize: '0.8rem' }}
                              >
                                +
                              </Box>
                            )}
                          </TableCell>
                        );
                      }

                      if (!kw) {
                        return <TableCell key={pillar} sx={cellSx} />;
                      }

                      return (
                        <TableCell key={pillar} sx={cellSx}>
                          {isEditing ? (
                            <TextField
                              inputRef={editInputRef}
                              size="small"
                              variant="standard"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onKeyDown={handleCellKeyDown}
                              onBlur={() => commitEdit()}
                              fullWidth
                              sx={{ '& input': { fontSize: '0.875rem', py: 0 } }}
                              autoFocus
                            />
                          ) : (
                            <Box
                              onDoubleClick={() => startEditCell('keyword', { keywordId: kw.id }, kw.keyword)}
                              sx={{ minHeight: 24, cursor: 'text', '&:hover .cell-delete': { opacity: 1 } }}
                            >
                              {kw.keyword}
                              <IconButton
                                className="cell-delete"
                                size="small"
                                sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, transition: 'opacity 0.2s', p: 0.25 }}
                                onClick={() => handleDeleteKeyword(kw.id)}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                    {/* Empty cell under "Add pillar" column */}
                    <TableCell sx={{ ...cellSx, borderRight: 'none' }} />
                  </TableRow>
                ))}
                {existingPillars.length === 0 && !editingCell && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No pillars configured. Click "Add pillar" to start.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ================================================================= */}
      {/* Tab 3: Data Collection                                              */}
      {/* ================================================================= */}
      {activeTab === 3 && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>Data Collection</Typography>

          {/* Section A: Collection Status */}
          <Typography variant="h6" sx={{ mb: 1 }}>Collection Status</Typography>
          <TableContainer component={Paper} sx={{ mb: 4, maxHeight: '50vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Platform</TableCell>
                  <TableCell sx={headerCellSx}>Account</TableCell>
                  <TableCell sx={headerCellSx}>Covered Range</TableCell>
                  <TableCell sx={headerCellSx}>Requested Range</TableCell>
                  <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Posts</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collectionStatus.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No collection data found.
                    </TableCell>
                  </TableRow>
                ) : collectionStatus.map((cs, i) => {
                  const level = getCoverageLevel(cs);
                  const isFullyCovered = level === 'complete';
                  const hasPartial = level === 'partial';
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ ...cellSx, textTransform: 'capitalize' }}>{cs.platform}</TableCell>
                      <TableCell sx={cellSx}>{cs.accountName}</TableCell>
                      <TableCell sx={cellSx}>
                        {cs.coveredStart ? `${fmtDate(cs.coveredStart)} - ${fmtDate(cs.coveredEnd)}` : 'No data'}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {fmtDate(cs.earliestRequested)} - {fmtDate(cs.latestRequested)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{cs.totalPosts.toLocaleString()}</TableCell>
                      <TableCell sx={cellSx}>
                        <Box sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.25,
                          borderRadius: 2,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: isFullyCovered ? 'rgba(76, 175, 80, 0.12)' : hasPartial ? 'rgba(255, 152, 0, 0.12)' : 'rgba(239, 83, 80, 0.12)',
                          color: isFullyCovered ? '#2e7d32' : hasPartial ? '#e65100' : '#c62828',
                        }}>
                          {isFullyCovered ? 'Complete' : hasPartial ? 'Partial' : 'No data'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section B: Account Alerts */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Account Alerts</Typography>
            <Button size="small" onClick={fetchAccountAlerts} sx={{ textTransform: 'none' }}>Refresh</Button>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: '50vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Platform</TableCell>
                  <TableCell sx={headerCellSx}>Account</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Message</TableCell>
                  <TableCell sx={{ ...headerCellSx, borderRight: 'none' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accountAlerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No alerts.
                    </TableCell>
                  </TableRow>
                ) : accountAlerts.map(alert => (
                  <TableRow
                    key={alert.id}
                    hover
                    sx={{
                      opacity: alert.resolved ? 0.5 : 1,
                      backgroundColor: alert.resolved ? undefined : 'rgba(239, 83, 80, 0.04)',
                    }}
                  >
                    <TableCell sx={cellSx}>
                      {alert.createdAt ? new Date(alert.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, textTransform: 'capitalize' }}>{alert.platform}</TableCell>
                    <TableCell sx={cellSx}>{alert.accountName}</TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{
                        display: 'inline-block',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 2,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(239, 83, 80, 0.12)',
                        color: '#c62828',
                      }}>
                        {alert.alertType}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'normal', maxWidth: 300 }}>{alert.message}</TableCell>
                    <TableCell sx={{ ...cellSx, borderRight: 'none' }}>
                      {!alert.resolved ? (
                        <Button size="small" onClick={() => handleResolveAlert(alert.id)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                          Resolve
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Resolved {alert.resolvedAt ? fmtDate(alert.resolvedAt) : ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section C: LinkedIn Collection Stats */}
          {linkedinStats.length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 1 }}>
                <Typography variant="h6">LinkedIn Collection</Typography>
                <Button size="small" onClick={() => { fetchLinkedinStats(); fetchLinkedinLogs(); }} sx={{ textTransform: 'none' }}>Refresh</Button>
              </Box>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Account</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Avg Posts/Day</TableCell>
                      <TableCell sx={headerCellSx}>Last Collected</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Next Request</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Total Posts</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                      <TableCell sx={{ ...headerCellSx, borderRight: 'none' }}>Last Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linkedinStats.map((s: any) => (
                      <TableRow key={s.brandMapId} hover>
                        <TableCell sx={cellSx}>{s.brandname}</TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{s.avgPostsPerDay ?? '---'}</TableCell>
                        <TableCell sx={cellSx}>
                          {s.lastCollectionAt ? new Date(s.lastCollectionAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{s.nextPostsToRequest}</TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{s.totalPostsCollected.toLocaleString()}</TableCell>
                        <TableCell sx={cellSx}>
                          <Box sx={{
                            display: 'inline-block', px: 0.75, py: 0.25, borderRadius: 2,
                            fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: s.isBackfill ? 'rgba(255, 152, 0, 0.12)' : s.consecutiveFailures > 0 ? 'rgba(239, 83, 80, 0.12)' : 'rgba(76, 175, 80, 0.12)',
                            color: s.isBackfill ? '#e65100' : s.consecutiveFailures > 0 ? '#c62828' : '#2e7d32',
                          }}>
                            {s.isBackfill ? 'Backfill' : s.consecutiveFailures > 0 ? `Failed (${s.consecutiveFailures})` : 'Active'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...cellSx, borderRight: 'none', whiteSpace: 'normal', maxWidth: 200, fontSize: '0.75rem', color: 'text.secondary' }}>
                          {s.lastError || '---'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Collection History toggle */}
              <Button
                size="small"
                onClick={() => { setShowLinkedinLogs(!showLinkedinLogs); if (!showLinkedinLogs && linkedinLogs.length === 0) fetchLinkedinLogs(); }}
                sx={{ textTransform: 'none', mb: 1 }}
              >
                {showLinkedinLogs ? 'Hide' : 'Show'} Collection History
              </Button>

              {showLinkedinLogs && (
                <TableContainer component={Paper} sx={{ maxHeight: '40vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Date</TableCell>
                        <TableCell sx={headerCellSx}>Account</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Requested</TableCell>
                        <TableCell sx={headerCellSx}>Reason</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Retrieved</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Posts/Day</TableCell>
                        <TableCell sx={headerCellSx}>Status</TableCell>
                        <TableCell sx={{ ...headerCellSx, borderRight: 'none' }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linkedinLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>No collection history yet.</TableCell>
                        </TableRow>
                      ) : linkedinLogs.map((log: any) => (
                        <TableRow key={log.id} hover>
                          <TableCell sx={cellSx}>
                            {log.startedAt ? new Date(log.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                          </TableCell>
                          <TableCell sx={cellSx}>{log.accountName}</TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{log.postsRequested}</TableCell>
                          <TableCell sx={cellSx}>
                            <Box sx={{
                              display: 'inline-block', px: 0.5, py: 0.15, borderRadius: 1,
                              fontSize: '0.65rem', fontWeight: 500,
                              backgroundColor: log.reason === 'backfill_250' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                              color: log.reason === 'backfill_250' ? '#e65100' : '#1565c0',
                            }}>
                              {log.reason}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{log.postsRetrieved}</TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{log.postsPerDayThisRun ?? '---'}</TableCell>
                          <TableCell sx={cellSx}>
                            <Box sx={{
                              display: 'inline-block', px: 0.5, py: 0.15, borderRadius: 1,
                              fontSize: '0.65rem', fontWeight: 600,
                              backgroundColor: log.status === 'success' ? 'rgba(76, 175, 80, 0.12)'
                                : log.status === 'cookie_expired' ? 'rgba(255, 152, 0, 0.12)'
                                : log.status === 'running' ? 'rgba(33, 150, 243, 0.12)'
                                : 'rgba(239, 83, 80, 0.12)',
                              color: log.status === 'success' ? '#2e7d32'
                                : log.status === 'cookie_expired' ? '#e65100'
                                : log.status === 'running' ? '#1565c0'
                                : '#c62828',
                            }}>
                              {log.status}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, borderRight: 'none', whiteSpace: 'normal', maxWidth: 180, fontSize: '0.7rem', color: 'text.secondary' }}>
                            {log.errorMessage || '---'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Tab 4: Whitelisted Domains                                         */}
      {/* ================================================================= */}
      {activeTab === 4 && organization && (
        <WhitelistedDomains
          organizationId={organization.id}
          organizationName={organization.name}
        />
      )}

      {/* ================================================================= */}
      {/* Tab 5: Usage                                                       */}
      {/* ================================================================= */}
      {activeTab === 5 && organization && (
        <OrgUsagePanel organizationId={organization.id} title={`Usage - ${organization.name}`} />
      )}

      {/* ================================================================= */}
      {/* Dialogs                                                            */}
      {/* ================================================================= */}

      {/* Add member dialog */}
      <Dialog open={openAddMember} onClose={() => setOpenAddMember(false)}>
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <TextField label="User Email" fullWidth sx={{ mt: 1 }} value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select value={newMemberRole} label="Role" onChange={e => setNewMemberRole(e.target.value as string)}>
              <MenuItem value="Owner">Owner</MenuItem>
              <MenuItem value="Billing Admin">Billing Admin</MenuItem>
              <MenuItem value="Brand Editor">Brand Editor</MenuItem>
              <MenuItem value="Viewer">Viewer</MenuItem>
              <MenuItem value="Trial User">Trial User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddMember(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMember}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit member dialog */}
      <Dialog open={openEditMember} onClose={() => setOpenEditMember(false)}>
        <DialogTitle>Edit Member</DialogTitle>
        <DialogContent>
          <TextField label="Email" fullWidth value={editingMember?.userEmail || ''} onChange={e => setEditingMember(editingMember ? { ...editingMember, userEmail: e.target.value } : null)} />
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select value={editingMember?.role || ''} label="Role" onChange={e => setEditingMember(editingMember ? { ...editingMember, role: e.target.value as string } : null)}>
              <MenuItem value="Owner">Owner</MenuItem>
              <MenuItem value="Billing Admin">Billing Admin</MenuItem>
              <MenuItem value="Brand Editor">Brand Editor</MenuItem>
              <MenuItem value="Viewer">Viewer</MenuItem>
              <MenuItem value="Trial User">Trial User</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel sx={{ mt: 2 }} control={<Switch checked={editingMember?.isActive || false} onChange={e => setEditingMember(editingMember ? { ...editingMember, isActive: e.target.checked } : null)} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditMember(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditMemberSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Account popover — inline editing for social accounts */}
      <Popover
        open={!!accountPopover}
        anchorEl={accountPopover?.anchorEl}
        onClose={closeAccountPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 340 } } }}
      >
        {accountPopover && (
          <Box>
            {/* Live URL preview */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {PLATFORM_URL_INFO[accountPopover.platform]?.hint || accountPopover.platform}
            </Typography>
            {popoverInput.trim() && (
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  px: 1,
                  py: 0.5,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  color: 'primary.main',
                }}
              >
                {getPopoverPreviewUrl()}
              </Typography>
            )}

            {/* Input field */}
            <TextField
              inputRef={popoverInputRef}
              size="small"
              fullWidth
              label="Account name or URL"
              placeholder={`e.g. nike or ${PLATFORM_URL_INFO[accountPopover.platform]?.buildUrl('nike')}`}
              value={popoverInput}
              onChange={e => { setPopoverInput(e.target.value); setPopoverError(''); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !popoverValidating) handlePopoverSave();
                if (e.key === 'Escape') closeAccountPopover();
              }}
              disabled={popoverValidating}
              error={!!popoverError}
              helperText={popoverError || (popoverValidating ? 'Validating account...' : ' ')}
              sx={{ mb: 1 }}
            />

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button size="small" onClick={closeAccountPopover} disabled={popoverValidating}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handlePopoverSave}
                disabled={popoverValidating || !popoverInput.trim()}
                startIcon={popoverValidating ? <CircularProgress size={14} /> : undefined}
              >
                {popoverValidating ? 'Validating...' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>

      {/* Conflict dialog — brand already has this platform */}
      <Dialog open={!!conflictDialog?.open} onClose={() => { setConflictDialog(null); cancelEdit(); }}>
        <DialogTitle>Duplicate Platform</DialogTitle>
        <DialogContent>
          <Typography>
            Brand <strong>"{conflictDialog?.brand}"</strong> already has a <strong>{conflictDialog?.platform}</strong> account: <strong>{conflictDialog?.existingAccountName}</strong>
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Replace it with <strong>{conflictDialog?.newAccountName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConflictDialog(null); cancelEdit(); }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConflictReplace}>Replace</Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================= */}
      {/* Snackbar                                                           */}
      {/* ================================================================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrganizationDetailsPage;
